'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Accessibility, Eye, AtSign, Phone } from 'lucide-react';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';

export default function AccessibilityPage() {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-background">
      {/* Background decoration */}
      <div className="absolute top-0 inset-x-0 h-[400px] bg-linear-to-br from-primary/10 via-background to-background pointer-events-none" />
      
      <div className="container max-w-4xl mx-auto px-4 pt-10 pb-20 relative z-10">
        <Link 
          href="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('master.back')}</span>
        </Link>
        
        <div className="bg-card border border-border shadow-xl rounded-3xl p-8 md:p-12">
          
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
            <Accessibility className="w-8 h-8 text-primary" />
          </div>

          <h1 className="text-3xl md:text-5xl font-black mb-4">
            הצהרת נגישות
          </h1>
          <p className="text-xl text-muted-foreground mb-10">
            הנגשת האתר לאנשים עם מוגבלויות היא בעדיפות עליונה עבורנו.
          </p>

          <div className="space-y-10">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-primary" />
                רמת הנגישות ב-YofiPlace
              </h2>
              <p className="text-muted-foreground leading-relaxed text-lg">
                אנו משקיעים מאמצים ומשאבים רבים על מנת לספק לכם שירות שוויוני, מכובד ונגיש. בהתאם לחוק שוויון זכויות לאנשים עם מוגבלויות תשנ&quot;ח-1998 בתקנות שהותקנו מכוחו, הושקעו מאמצים משמעותיים בהנגשת אתר זה לאנשים עם מוגבלויות.
                האתר עומד בדרישות תקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע&quot;ג 2013.
                התאמות הנגישות בוצעו עפ&quot;י המלצות התקן הישראלי (ת&quot;י 5568) לנגישות תכנים באינטרנט ברמת AA מסמך WCAG2.0.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold flex items-center gap-3 mb-4">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-primary font-bold text-sm">✓</span>
                </div>
                אמצעי הנגישות באתר
              </h2>
              <ul className="grid md:grid-cols-2 gap-4 text-muted-foreground text-lg">
                <li className="flex items-start gap-3 bg-secondary/50 p-4 rounded-2xl">
                  <span className="text-primary mt-1 font-bold">•</span>
                  <span>ניתוב קל באמצעות מקלדת - מתאפשרת הפעלה וניווט באמצעות המקלדת בלבד.</span>
                </li>
                <li className="flex items-start gap-3 bg-secondary/50 p-4 rounded-2xl">
                  <span className="text-primary mt-1 font-bold">•</span>
                  <span>תמיכה בטכנולוגיות מסייעות כגון תוכנות קוראות מסך (Screen readers).</span>
                </li>
                <li className="flex items-start gap-3 bg-secondary/50 p-4 rounded-2xl">
                  <span className="text-primary mt-1 font-bold">•</span>
                  <span>עיצוב ברור וניגודיות צבעים המקלים על קריאת הטקסט ללקויי ראייה.</span>
                </li>
                <li className="flex items-start gap-3 bg-secondary/50 p-4 rounded-2xl">
                  <span className="text-primary mt-1 font-bold">•</span>
                  <span>התאמה מלאה למסכים ניידים ורזולוציות שונות.</span>
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="bg-primary/5 rounded-3xl p-6 md:p-8 border border-primary/10">
              <h2 className="text-2xl font-bold mb-4">
                פניות ודיווח על תקלות נגישות
              </h2>
              <p className="text-muted-foreground leading-relaxed text-lg mb-6">
                על אף מאמצינו להנגיש את כלל הדפים והאלמנטים באתר, ייתכן שיתגלו חלקים מסוימים שאינם מונגשים כראוי, או שטרם הונגשו. 
                אם נתקלתם בבעיית נגישות באתר – נשמח לקבל פנייה ולהעניק פתרון הולם!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="mailto:support@yofiplace.co.il" 
                  className="flex-1 bg-card hover:bg-secondary border border-border p-4 rounded-2xl flex items-center gap-4 transition-all group"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <AtSign className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold text-foreground">דוא&quot;ל פניות</p>
                    <p className="text-sm text-primary">support@yofiplace.co.il</p>
                  </div>
                </a>
                
                <a 
                  href="tel:+972500000000" 
                  className="flex-1 bg-card hover:bg-secondary border border-border p-4 rounded-2xl flex items-center gap-4 transition-all group"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold text-foreground">טלפון</p>
                    <p className="text-sm text-primary">050-0000000</p>
                  </div>
                </a>
              </div>
            </section>

            <div className="text-sm text-muted-foreground border-t border-border pt-6">
              תאריך עדכון אחרון: {new Date().toLocaleDateString('he-IL')}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
